import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Building2, MapPin, ChevronDown, ChevronUp } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Button } from '../components/ui/button';

const Companies = () => {
  const { user } = useContext(AuthContext);
  const [companies, setCompanies] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [branchDialogOpen, setBranchDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [editingBranch, setEditingBranch] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [expandedCompany, setExpandedCompany] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    ruc: '',
    address: '',
    phone: '',
    email: '',
    status: 'active'
  });
  const [branchFormData, setBranchFormData] = useState({
    company_id: '',
    name: '',
    address: '',
    phone: '',
    manager: '',
    status: 'active'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [companiesRes, branchesRes] = await Promise.all([
        axios.get(`${API}/companies`),
        axios.get(`${API}/branches`)
      ]);
      setCompanies(companiesRes.data);
      setBranches(branchesRes.data);
    } catch (error) {
      toast.error('Error al cargar empresas');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCompany) {
        await axios.put(`${API}/companies/${editingCompany.id}`, formData);
        toast.success('Empresa actualizada');
      } else {
        await axios.post(`${API}/companies`, formData);
        toast.success('Empresa creada');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleBranchSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBranch) {
        await axios.put(`${API}/branches/${editingBranch.id}`, branchFormData);
        toast.success('Sucursal actualizada');
      } else {
        await axios.post(`${API}/branches`, { ...branchFormData, company_id: selectedCompany.id });
        toast.success('Sucursal creada');
      }
      setBranchDialogOpen(false);
      resetBranchForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta empresa?')) return;
    try {
      await axios.delete(`${API}/companies/${id}`);
      toast.success('Empresa eliminada');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      ruc: '',
      address: '',
      phone: '',
      email: '',
      status: 'active'
    });
    setEditingCompany(null);
  };

  const resetBranchForm = () => {
    setBranchFormData({
      company_id: '',
      name: '',
      address: '',
      phone: '',
      manager: '',
      status: 'active'
    });
    setEditingBranch(null);
    setSelectedCompany(null);
  };

  const openEditDialog = (company) => {
    setEditingCompany(company);
    setFormData({
      name: company.name,
      ruc: company.ruc || '',
      address: company.address || '',
      phone: company.phone || '',
      email: company.email || '',
      status: company.status || 'active'
    });
    setDialogOpen(true);
  };

  const openBranchDialog = (company) => {
    setSelectedCompany(company);
    setBranchDialogOpen(true);
  };

  const getCompanyBranches = (companyId) => {
    return branches.filter(b => b.company_id === companyId);
  };

  return (
    <div data-testid="companies-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Empresas y Sucursales
          </h1>
          <p className="text-slate-600 mt-2">Gestionar organizaciones del sistema</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-company-button" className="btn-primary">
              <Plus size={20} className="mr-2" />
              Nueva Empresa
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                {editingCompany ? 'Editar Empresa' : 'Nueva Empresa'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Nombre *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input-field"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">NIT / RUC</label>
                  <input
                    type="text"
                    value={formData.ruc}
                    onChange={(e) => setFormData({ ...formData, ruc: e.target.value })}
                    className="input-field"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Teléfono</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="input-field"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Correo</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="input-field"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Dirección</label>
                <textarea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="input-field"
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Estado</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="input-field"
                >
                  <option value="active">Activa</option>
                  <option value="inactive">Inactiva</option>
                </select>
              </div>
              <div className="flex justify-end space-x-3">
                <Button type="button" onClick={() => { setDialogOpen(false); resetForm(); }} className="btn-secondary">
                  Cancelar
                </Button>
                <Button type="submit" className="btn-primary">
                  {editingCompany ? 'Actualizar' : 'Crear Empresa'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Branch Dialog */}
      <Dialog open={branchDialogOpen} onOpenChange={(open) => { setBranchDialogOpen(open); if (!open) resetBranchForm(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {editingBranch ? 'Editar Sucursal' : `Nueva Sucursal - ${selectedCompany?.name}`}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleBranchSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Nombre *</label>
              <input
                type="text"
                value={branchFormData.name}
                onChange={(e) => setBranchFormData({ ...branchFormData, name: e.target.value })}
                className="input-field"
                placeholder="Ej: Sucursal Centro"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Teléfono</label>
                <input
                  type="tel"
                  value={branchFormData.phone}
                  onChange={(e) => setBranchFormData({ ...branchFormData, phone: e.target.value })}
                  className="input-field"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Encargado</label>
                <input
                  type="text"
                  value={branchFormData.manager}
                  onChange={(e) => setBranchFormData({ ...branchFormData, manager: e.target.value })}
                  className="input-field"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Dirección</label>
              <textarea
                value={branchFormData.address}
                onChange={(e) => setBranchFormData({ ...branchFormData, address: e.target.value })}
                className="input-field"
                rows={2}
              />
            </div>
            <div className="flex justify-end space-x-3">
              <Button type="button" onClick={() => { setBranchDialogOpen(false); resetBranchForm(); }} className="btn-secondary">
                Cancelar
              </Button>
              <Button type="submit" className="btn-primary">
                {editingBranch ? 'Actualizar' : 'Crear Sucursal'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Companies List */}
      {loading ? (
        <div className="text-center py-12">Cargando...</div>
      ) : (
        <div className="space-y-4">
          {companies.map((company) => {
            const companyBranches = getCompanyBranches(company.id);
            return (
              <div key={company.id} className="bg-white border border-slate-200 rounded-md overflow-hidden">
                <div
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50"
                  onClick={() => setExpandedCompany(expandedCompany === company.id ? null : company.id)}
                >
                  <div className="flex items-center">
                    <Building2 size={24} className="text-orange-500 mr-3" />
                    <div>
                      <h3 className="font-semibold text-slate-800">{company.name}</h3>
                      <div className="flex items-center text-sm text-slate-500 space-x-4">
                        {company.ruc && <span>NIT: {company.ruc}</span>}
                        {company.phone && <span>Tel: {company.phone}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className={`status-badge ${company.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {company.status === 'active' ? 'Activa' : 'Inactiva'}
                    </span>
                    <span className="text-sm text-slate-500">
                      {companyBranches.length} sucursal(es)
                    </span>
                    <button
                      onClick={(e) => { e.stopPropagation(); openEditDialog(company); }}
                      className="p-2 hover:bg-slate-100 rounded"
                    >
                      <Edit size={18} className="text-orange-500" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(company.id); }}
                      className="p-2 hover:bg-red-50 rounded"
                    >
                      <Trash2 size={18} className="text-red-500" />
                    </button>
                    {expandedCompany === company.id ? (
                      <ChevronUp size={20} className="text-slate-400" />
                    ) : (
                      <ChevronDown size={20} className="text-slate-400" />
                    )}
                  </div>
                </div>

                {/* Expanded Branches */}
                {expandedCompany === company.id && (
                  <div className="border-t border-slate-200 p-4 bg-slate-50">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-sm font-semibold text-slate-700">Sucursales</h4>
                      <button
                        onClick={() => openBranchDialog(company)}
                        className="text-sm text-orange-500 hover:text-orange-600 flex items-center"
                      >
                        <Plus size={16} className="mr-1" />
                        Agregar Sucursal
                      </button>
                    </div>
                    {companyBranches.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {companyBranches.map((branch) => (
                          <div key={branch.id} className="bg-white p-3 rounded border border-slate-200">
                            <div className="flex items-start justify-between">
                              <div className="flex items-start">
                                <MapPin size={16} className="text-orange-400 mr-2 mt-0.5" />
                                <div>
                                  <p className="font-medium text-slate-800">{branch.name}</p>
                                  {branch.address && (
                                    <p className="text-xs text-slate-500">{branch.address}</p>
                                  )}
                                  {branch.manager && (
                                    <p className="text-xs text-slate-500">Encargado: {branch.manager}</p>
                                  )}
                                </div>
                              </div>
                              <span className={`text-xs px-2 py-1 rounded ${branch.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                {branch.status === 'active' ? 'Activa' : 'Inactiva'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500 text-center py-4">No hay sucursales registradas</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {companies.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <Building2 size={48} className="mx-auto mb-4 text-slate-300" />
              <p>No hay empresas registradas</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Companies;
